/**
 * Personal referral page at `/me/referrals`.
 *
 * Surface for any authenticated user to grab their unique referral
 * link, share it, watch the leaderboard, and (eventually) earn
 * platform credit when someone they referred completes a paid action.
 *
 * Sources data from the existing `mega.py` referral endpoints
 * (`GET /api/mega/me/referral`) + the public leaderboard at
 * `/api/v2/referrals/leaderboard`. We add a QR code generator
 * (client-side via a Google Chart API call — same approach used in
 * the existing share-my-kennel widget; no extra dep).
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShareIcon as Share, QrCodeIcon as QrCode, TrophyIcon as Trophy, CopyIcon as Copy, FacebookLogoIcon as FacebookLogo, InstagramLogoIcon as InstagramLogo, TwitterLogoIcon as TwitterLogo, EnvelopeSimpleIcon as EnvelopeSimple, WhatsappLogoIcon as WhatsappLogo, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

export default function MyReferrals() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/me/referral")
      .then((r) => setMe(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load your referral data"));
    api.get("/referrals/leaderboard")
      .then((r) => setBoard(r.data?.leaderboard || []))
      .catch(() => setBoard([]));
  }, []);

  const shareUrl = me?.share_url || "";
  const qrUrl = useMemo(() => {
    if (!shareUrl) return "";
    // qrserver is a public, CORS-friendly QR generator. No API key required.
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=8&data=${encodeURIComponent(shareUrl)}`;
  }, [shareUrl]);

  const copy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => toast.success("Link copied."));
  };

  const shareText = encodeURIComponent(
    "Just registered my dog with Armstrong K9 Registry — clean platform with real pedigree + DNA-verified status. Use my link 👇",
  );
  const socials = useMemo(() => [
    { I: FacebookLogo, label: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { I: TwitterLogo,  label: "X",        url: `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}` },
    { I: WhatsappLogo, label: "WhatsApp", url: `https://api.whatsapp.com/send?text=${shareText}%20${encodeURIComponent(shareUrl)}` },
    { I: EnvelopeSimple, label: "Email",  url: `mailto:?subject=Armstrong%20K9%20Registry&body=${shareText}%20${encodeURIComponent(shareUrl)}` },
    { I: InstagramLogo, label: "IG bio",  url: shareUrl, copyOnly: true },
  ], [shareUrl, shareText]);

  if (err) return <AppShell><p className="max-w-3xl mx-auto px-6 py-12 text-sm text-[#C1272D]">{err}</p></AppShell>;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold flex items-center gap-1.5">
          <Share size={12} weight="duotone" /> Refer a friend
        </p>
        <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-1">Your referral kit</h1>
        <p className="text-sm text-[#666] max-w-2xl mt-2">
          {isNativeApp()
            ? "Share your personal link. When friends register a dog, you earn platform credit."
            : "Share your personal link. When friends register a dog, you earn platform credit toward your next subscription, registration, or course."}
        </p>

        {!me ? (
          <div className="mt-8 grid md:grid-cols-2 gap-4"><Skeleton className="h-72" /><Skeleton className="h-72" /></div>
        ) : (
          <div className="mt-8 grid md:grid-cols-[1fr_auto] gap-6 bg-white border border-[#EAEAEA] rounded-2xl p-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#888]">Your personal link</p>
              <p className="mt-1.5 font-mono text-sm break-all bg-[#FAFAFA] border border-[#EFEFEF] rounded-lg px-3 py-2.5" data-testid="referral-link">
                {shareUrl}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button onClick={copy} variant="outline" className="rounded-full" data-testid="referral-copy">
                  <Copy size={14} className="mr-1.5" /> Copy
                </Button>
                {socials.map(({ I, label, url, copyOnly }) => (
                  <a
                    key={label}
                    href={url}
                    onClick={(e) => {
                      if (copyOnly) {
                        e.preventDefault();
                        copy();
                      }
                    }}
                    target={copyOnly ? "_self" : "_blank"}
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] px-3 py-1.5 text-xs hover:border-[#0E0E0E]"
                    data-testid={`referral-share-${label.toLowerCase()}`}
                  >
                    <I size={14} /> {label}
                  </a>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2 max-w-md">
                <Stat label="Visits"     value={me.visits} testid="referral-visits" />
                <Stat label="Conversions" value={me.conversions} testid="referral-conversions" />
                <Stat label="Earned"     value={`$${(me.earned || 0).toFixed(2)}`} testid="referral-earned" />
              </div>
              {me.pending_commission > 0 && (
                <p className="text-[12px] text-[#888] mt-2">
                  <strong>${(me.pending_commission).toFixed(2)}</strong> pending payout (clears when the buyer's escrow releases).
                </p>
              )}
            </div>
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-[#888] mb-2 flex items-center gap-1 justify-center">
                <QrCode size={11} /> QR code
              </p>
              {qrUrl ? (
                <img src={qrUrl} alt="QR code for your referral link" className="rounded-xl border border-[#EAEAEA] w-44 h-44" data-testid="referral-qr" />
              ) : null}
              <a
                href={qrUrl}
                download="armstrong-k9-referral-qr.png"
                className="inline-block mt-2 text-xs underline text-[#666]"
                data-testid="referral-qr-download"
              >
                Download QR
              </a>
            </div>
          </div>
        )}

        <div className="mt-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#888] font-bold flex items-center gap-1.5 mb-3">
            <Trophy size={12} weight="duotone" /> Leaderboard · top referrers
          </p>
          {board.length === 0 ? (
            <p className="text-sm text-[#666] bg-white border border-[#EAEAEA] rounded-2xl p-6 text-center" data-testid="referral-board-empty">
              No referrals yet — be the first to land on the board.
            </p>
          ) : (
            <ol className="bg-white border border-[#EAEAEA] rounded-2xl divide-y divide-[#F3F3F3]" data-testid="referral-leaderboard">
              {board.slice(0, 25).map((row, i) => (
                <li key={row.user_id || i} className="flex items-baseline justify-between gap-3 px-4 py-2.5">
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="font-tabular font-semibold text-[#888] w-6">#{i + 1}</span>
                    <span className="font-spectral truncate">{row.display_name || "Anonymous breeder"}</span>
                  </span>
                  <span className="font-tabular font-semibold text-sm">{row.count} refs</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="mt-10 bg-[#FBF7EC] border border-gold/40 rounded-2xl p-5 text-sm">
          <p className="font-spectral text-lg">How the credit works</p>
          <ul className="mt-2 space-y-1 text-[#3F3F3F]">
            <li>· Visits are tracked the moment someone clicks your link.</li>
            {isNativeApp() ? (
              <>
                <li>· A conversion records when they sign up + complete a paid action.</li>
                <li>· You earn 1% of the converting purchase as platform credit (capped at $50/conversion).</li>
                <li>· Credit auto-applies — no manual claim.</li>
              </>
            ) : (
              <>
                <li>· A conversion records when they sign up + complete a paid action ($25 registration, paid subscription, course purchase, or escrow release).</li>
                <li>· You earn 1% of the converting purchase as platform credit (capped at $50/conversion).</li>
                <li>· Credit auto-applies to your next subscription renewal — no manual claim.</li>
              </>
            )}
          </ul>
          <a href="/admin/growth/playbook#clients" className="inline-flex items-center gap-1.5 text-xs underline text-[#7a5a00] mt-3" data-testid="referral-playbook-link">
            Read the share-this-link playbook <ArrowRight size={11} />
          </a>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-[#FAFAFA] border border-[#EFEFEF] rounded-xl px-3 py-2 text-center" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#888]">{label}</p>
      <p className="font-spectral text-xl tracking-tight">{value}</p>
    </div>
  );
}
